import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import {pathOr, clone} from 'ramda';

import {DY_HOST, DY_HOST_ENGAGEMENT} from 'features/experiments/constants';
import {getCookie} from 'libs/getCookie';
import Store from 'store/storages/cookieStorage';

import {KUBERNETES_USER_AGENT} from 'constants/api';
import {cookieStore} from 'libs/storage/storage';
import {isHtoEnabled} from 'constants/flags/flags';
import {FIRST_DAY_COOKIE, TEST_DY_COOKIE, REAL_IP} from 'constants/cookie';
import {getUrlParameter} from 'libs/url';
import {instanceDyChoose, instanceDyEngagement} from 'libs/api';

export type RequestDataType = {
    selector?: {[key: string]: unknown};
    user?: {[key: string]: unknown};
    session?: {[key: string]: unknown};
    context?: {[key: string]: unknown};
};

/**
 * Get user data
 * @param {RequestDataType} data - request data
 * @returns {{dyid: string, dyid_server: string;}} User data
 */
export const getUserData = (data?: RequestDataType) => {
    const DY_ID = __NODEJS__ ? cookieStore.get('_dyid') : getCookie('_dyid');
    const DY_SERVER_ID = __NODEJS__ ? cookieStore.get('_dyid_server') : getCookie('_dyid_server');

    return {
        dyid: DY_ID || data?.user?.dyid || '',
        dyid_server: DY_SERVER_ID ? String(DY_SERVER_ID) : data?.user?.dyid_server || '',
    };
};

/**
 * Get session data
 * @param {RequestDataType} data - request data
 * @returns {{session: {dy: string}}} Session data
 */
export const getSessionData = (data?: RequestDataType) => {
    const DY_SESSION_ID = __NODEJS__ ? cookieStore.get('_dyjsession') : getCookie('_dyjsession');

    return {
        session: {dy: DY_SESSION_ID || data?.session?.dy || ''},
    };
};

/**
 * Get context for an experiment
 * @param {string} url - Base URL
 * @param {RequestDataType} data - Current data
 * @returns {Object} Context or empty object
 */
export const getContextData = (url?: string, data?: RequestDataType) => {
    const genericPageAttributes = {
        isHto: String(isHtoEnabled()),
        isFirstDay: String(cookieStore.get(FIRST_DAY_COOKIE, '')),
        testDY: String(cookieStore.get(TEST_DY_COOKIE, '')),
        release: String(process.env.RELEASE || ''),
    };

    return url === DY_HOST
        ? {
              context: {
                  page: {
                      location: pathOr(url, ['location', 'href'], window),
                      locale: 'en_US',
                      data: [],
                      referrer: document.referrer || '',
                      ...pathOr({}, ['context', 'page'], data),
                  },
                  device: {
                      userAgent: __NODEJS__
                          ? cookieStore.get('SSR_USER_AGENT') || 'SSR_USER_AGENT_PRODUCT_PAGE'
                          : pathOr('', ['navigator', 'userAgent'], window),
                      ip: String(cookieStore.get(REAL_IP, '')),
                      ...pathOr({}, ['context', 'device'], data),
                  },
                  pageAttributes: {
                      ...genericPageAttributes,
                      ...pathOr({}, ['context', 'pageAttributes'], data),
                  },
              },
          }
        : {};
};

/**
 * Prepares data to selector
 * @param {RequestDataType} data - Current data
 * @returns {Object} Selector data
 */
export const getSelectorData = (data?: RequestDataType) => {
    const dyPreviewId = getUrlParameter('dyApiPreview');
    const preview = dyPreviewId ? {preview: {ids: [dyPreviewId]}} : {};

    return {
        selector: {
            ...preview,
            ...(data?.selector ?? {}),
        },
    };
};

/**
 * Preparing data for the request body
 * @param {AxiosRequestConfig} request - Request
 * @returns {Object} Modified data
 */
export const prepareRequestData = ({
    data,
    baseURL,
}: AxiosRequestConfig<RequestDataType>): {[key: string]: unknown} => ({
    ...data,
    user: getUserData(data),
    ...getSessionData(data),
    ...getContextData(baseURL, data),
    ...getSelectorData(data),
});

/**
 * Preparing data for the engagement request body
 * @param {AxiosRequestConfig} request - Request
 * @returns {Object} Modified data
 */
export const prepareEngagementRequestData = ({
    data,
}: AxiosRequestConfig): {[key: string]: unknown} => ({
    ...data,
    ...getSessionData(),
    user: getUserData(),
    context: {
        device: {
            ip: String(cookieStore.get(REAL_IP, '')),
        },
    },
});

/**
 * Supplementing the request to DY API with required fields
 * @param {AxiosRequestConfig} request - Request
 * @returns {AxiosRequestConfig} Modified request
 */
export const prepareRequest = (request: AxiosRequestConfig): AxiosRequestConfig => {
    if (request.baseURL !== DY_HOST) return request;

    // !__NODEJS__ check is needed while 'browserMock' is existing
    if (!__NODEJS__ && (window?.navigator?.userAgent || '').includes(KUBERNETES_USER_AGENT))
        return {
            ...request,
            cancelToken: new axios.CancelToken(cancel => cancel('Cancel request for Kubernetes')),
        };

    const modifiedRequest = clone(request);

    modifiedRequest.data = prepareRequestData(request);

    return modifiedRequest;
};

/**
 * Supplementing the engagement request to DY API with required fields
 * @param {AxiosRequestConfig} request - Request
 * @returns {AxiosRequestConfig} Modified request
 */
export const prepareEngagementRequest = (request: AxiosRequestConfig): AxiosRequestConfig => {
    if (request.baseURL !== DY_HOST_ENGAGEMENT) return request;

    const modifiedRequest = clone(request);

    if (!__NODEJS__) modifiedRequest.data = prepareEngagementRequestData(request);

    return modifiedRequest;
};

/**
 * Identify a new user without loading DY based on the DY API response
 * @param {AxiosResponse<any>} response - response
 * @returns {AxiosResponse<any>} response
 */
export const newUserIdentification = (response: AxiosResponse<any>): AxiosResponse<any> => {
    if (__NODEJS__) return response;

    const DY_SERVER_ID = getCookie('_dyid_server');

    if (response.config.baseURL === DY_HOST && !DY_SERVER_ID && response.data?.cookies?.length) {
        response.data.cookies.forEach(({name, value}) => {
            Store.write(name, value);
        });
    }

    return response;
};

instanceDyChoose.interceptors.request.use(prepareRequest);
instanceDyEngagement.interceptors.request.use(prepareEngagementRequest);
instanceDyChoose.interceptors.response.use(newUserIdentification);
